/**
 * @file CartBar.tsx
 * @description Sticky bottom bar for the Learning Materials cart. Always pinned to the
 * viewport bottom, it shows the current bag status and a button that opens the order
 * summary panel. Reads all state from CartContext.
 */

'use client';

import { useEffect, useState } from 'react';
import { cartBarVariants } from './LearningMaterials.styles';
import { injectNumberOfItemsWithinLabel, useCart } from './CartProvider';
import { OrderSummary } from './OrderSummary';
import { IForms, ILearningMaterials } from '@/.generated';
import { trackAdobeClick } from '@/utils/adobe/click';

type CartBarProps = Pick<
  NonNullable<ILearningMaterials['order_summary']>,
  'summary_bar_title' | 'view_order_button_label' | 'empty_cart_label'
> & {
  orderSummaryProps: ILearningMaterials['order_summary'];
  forms?: IForms;
};

export const CartBar = ({
  summary_bar_title,
  view_order_button_label,
  orderSummaryProps,
  empty_cart_label,
  forms,
}: CartBarProps) => {
  const { isOpen, totalItems, openCart } = useCart();

  // Once the order summary panel has been opened, keep it mounted (just hidden) so
  // in-progress form input isn't wiped by an unmount when the panel is closed and reopened
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  useEffect(() => {
    if (isOpen) setHasOpenedOnce(true);
  }, [isOpen]);

  const styles = cartBarVariants();

  // Track the click then open the order summary panel
  const handleViewOrder = (e: React.MouseEvent<HTMLButtonElement>) => {
    trackAdobeClick(
      { clickName: view_order_button_label || 'View order', clickLocation: 'Learning-Materials-Cart', clickType: 'cart-button' },
      e
    );
    openCart();
  };

  return (
    <>
      {!isOpen && (
        <div className={styles.bar()}>
          <div className={styles.inner()}>
            {summary_bar_title && (
              <p className={styles.summaryText()} aria-live="polite">
                {totalItems > 0
                  ? injectNumberOfItemsWithinLabel(summary_bar_title, totalItems)
                  : empty_cart_label}
              </p>
            )}
            <button
              type="button"
              className={styles.viewButton()}
              onClick={handleViewOrder}
              aria-haspopup="dialog"
              aria-expanded={isOpen}
            >
              {view_order_button_label}
            </button>
          </div>
        </div>
      )}

      {hasOpenedOnce && <OrderSummary {...orderSummaryProps} forms={forms} isOpen={isOpen} />}
    </>
  );
};
